'use client';

import { useEffect, type MouseEvent, type ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { useMediaQuery } from 'react-responsive';
import { useWebChat, useWebChatContent } from '@fshr/hooks';
import { useTranslations, useLocale } from '@fshr/context/i18n-context';
import { useTealium } from '@fshr/context/tealium-context';
import { cleanPhoneNumber, formatPhoneNumberInternational } from '@fshr/shared-react';
import IntroAndConversationCard, {
	type IntroProps,
	type IconListProps,
	type ConversationCardProps,
	type ConversationCardCta,
	KeyFact,
} from '../intro-and-conversation-card';
import { ImagePrimitive } from '@fshr/shared/types';
import { UserStore, journeyStore, type MostRecentSearch } from '@micro-apps/stores';
import {
	formatDateInLocale,
	getDateFormat,
	getMonthFormat,
	mapAccommodationsPagePath,
	simpleLinkRewrite,
	type FSLocale,
} from '@fshr/shared';
import { getPlpLayoutVariant } from '../plp-analytics';

export type ConversationPhase = 'discovery' | 'planning' | 'shopping';

export type IntroContentProps = Omit<IntroProps, 'links'> & {
	phoneNumber: string;
	locationHref: string;
	contactUsHref: string;
};

export type Props = {
	intro: IntroContentProps;
	positioning: KeyFact[];
	conversationCardImage: ImagePrimitive;
	propertyCode: string;
	propertyPath: string;
	interactionType?: string;
	sectionId?: string;
	userStore?: UserStore;
};

type Translate = ReturnType<typeof useTranslations>;

function resolvePhase(journeyStage?: 'DISCOVERY' | 'PLANNING' | 'SHOPPING'): ConversationPhase {
	switch (journeyStage) {
		case 'SHOPPING':
			return 'shopping';
		case 'PLANNING':
			return 'planning';
		default:
			return 'discovery';
	}
}

const DAY_FIRST_LOCALES: FSLocale[] = ['jp', 'kr', 'zh', 'zh_hant'];

function formatTravelDate(date: string, locale: string): string {
	const useLocale = locale as FSLocale;
	const pattern = DAY_FIRST_LOCALES.includes(useLocale)
		? `${getMonthFormat({ useLocale, responsive: false })}${getDateFormat({ useLocale, responsive: false })} EEE`
		: 'EEE, MMM d';
	return formatDateInLocale(date, pattern, 'yyyy-MM-dd', useLocale);
}

function formatTravelDates(checkin: string, checkout: string, locale: string): string {
	return `${formatTravelDate(checkin, locale)} – ${formatTravelDate(checkout, locale)}`;
}

function getResumeSearchHref(
	propertyPath: string,
	propertyCode: string,
	locale: string,
	{ checkin, checkout, rooms }: MostRecentSearch
): string {
	const accommodationsPath = mapAccommodationsPagePath(`${propertyPath}/accommodations`) ?? '';

	let search = `?generalReservationForm.locationId=${propertyCode}&generalReservationForm.checkInDate=${checkin}&generalReservationForm.checkOutDate=${checkout}&cawConfig.customBooker=true`;

	const guestRooms = rooms.length ? rooms : [{ adults: 2, children: 0 }];
	guestRooms.forEach(({ adults = 2, children = 0 }, i) => {
		search = `${search}&generalReservationForm.guestCountPerRoom[${i}].adultCount=${adults}`;
		search = `${search}&generalReservationForm.guestCountPerRoom[${i}].childCount=${children}`;
	});

	return `/content/fshr/pages/${locale}/properties/${accommodationsPath}${search}`;
}

function createLocationScrollHandler(locationHref: string) {
	return (event: MouseEvent<HTMLElement>) => {
		if (!locationHref.startsWith('#')) return;
		const target = document.querySelector(locationHref);
		if (target) {
			event.preventDefault();
			// Mirror the same offset logic used by JumpLinkStickyTab (use-jump-link-nav.ts)
			// to account for the sticky page header and the jump-link nav bar.
			const pageHeader = document.querySelector<HTMLElement>('#TopNav-mainNav');
			const jumpNav = document.querySelector<HTMLElement>('[data-jump-link-sticky-tab]');
			const offset =
				(pageHeader?.getBoundingClientRect().height ?? 0) +
				(jumpNav?.getBoundingClientRect().height ?? 0);
			window.scrollTo({
				top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset),
				behavior: 'smooth',
			});
		}
	};
}

function resolveIntroLinks(
	intro: Pick<IntroContentProps, 'phoneNumber' | 'locationHref' | 'contactUsHref'>,
	chatEnabled: boolean,
	t: Translate
): ConversationCardCta[] {
	const links: ConversationCardCta[] = [];

	if (intro.phoneNumber) {
		links.push({
			label: formatPhoneNumberInternational(intro.phoneNumber),
			href: `tel:${cleanPhoneNumber(intro.phoneNumber)}`,
		});
	}

	if (intro.locationHref) {
		links.push({
			label: t('Location'),
			href: intro.locationHref,
			onClick: intro.locationHref.startsWith('#')
				? createLocationScrollHandler(intro.locationHref)
				: undefined,
		});
	}

	if (chatEnabled && intro.contactUsHref) {
		links.push({ label: t('Contact Us'), href: intro.contactUsHref });
	}

	return links;
}

type ResolveConversationCardArgs = {
	image: ImagePrimitive;
	phase: ConversationPhase;
	mostRecentSearch: MostRecentSearch | undefined;
	chatAvailable: boolean;
	contactUsHref: string;
	isSignedIn: boolean;
	isMobile: boolean;
	locale: string;
	propertyCode: string;
	propertyPath: string;
	onChat: () => void;
	trackCta: (detail: string) => void;
	t: Translate;
};

function resolveConversationCard({
	image,
	phase,
	mostRecentSearch,
	chatAvailable,
	contactUsHref,
	isSignedIn,
	isMobile,
	locale,
	propertyCode,
	propertyPath,
	onChat,
	trackCta,
	t,
}: ResolveConversationCardArgs): ConversationCardProps {
	const isShopping = phase === 'shopping';
	// Only the shopping phase with a saved search puts "Resume search" in the
	// primary slot; every other case there falls back to nonShoppingPrimaryCta.
	const hasResumeSearch = isShopping && Boolean(mostRecentSearch);

	const defaultCopy: Record<ConversationPhase, string> = {
		discovery: t('Welcome, your next trip awaits.'),
		planning: t('Welcome back, your next trip awaits.'),
		shopping: t('Welcome back, let us help you with your booking.'),
	};

	const primaryOrSecondaryCta: ConversationCardCta = {
		label: chatAvailable ? t('Chat with live concierge') : t('Contact Us'),
		intent: isMobile ? 'outline' : undefined,
		onClick: () => {
			trackCta(chatAvailable ? 'web chat' : 'contact us');
			if (chatAvailable) onChat();
		},
		href: chatAvailable ? undefined : contactUsHref,
	};

	// On mobile, the primary slot collapses onto the same contact/chat CTA as
	// the secondary slot — "Check rates" only shows as its own button on desktop.
	const nonShoppingPrimaryCta: ConversationCardCta = isMobile
		? primaryOrSecondaryCta
		: {
				label: t('Check Rates'),
				onClick: () => {
					trackCta('check rates');
					window.dispatchEvent(new Event('cawComponentOpen'));
				},
			};

	return {
		image,
		heading: defaultCopy[phase],
		infoBlock:
			isShopping && mostRecentSearch
				? {
						heading: t('Check In - Check Out'),
						text: formatTravelDates(mostRecentSearch.checkin, mostRecentSearch.checkout, locale),
						cta: {
							label: t('Change Dates'),
							onClick: () =>
								window.dispatchEvent(
									new CustomEvent('cawComponentOpen', { detail: { setFocusTo: 'dates' } })
								),
						},
					}
				: undefined,
		primaryCta:
			hasResumeSearch && mostRecentSearch
				? {
						label: t('Resume Search'),
						href: getResumeSearchHref(propertyPath, propertyCode, locale, mostRecentSearch),
						onClick: () => {
							trackCta('resume search');
						},
					}
				: nonShoppingPrimaryCta,

		// The contact/chat CTA always gets its own slot on desktop. On mobile it
		// normally collapses into the primary slot (see nonShoppingPrimaryCta),
		// but when "Resume search" owns the primary slot it still needs a home,
		// so keep it as the secondary button there too.
		secondaryCta: isMobile && !hasResumeSearch ? undefined : primaryOrSecondaryCta,
		tertiaryCta: isShopping
			? undefined
			: {
					label: t('I already have a reservation'),
					onClick: () => trackCta('already have a reservation'),
					href: simpleLinkRewrite(
						isSignedIn
							? `/content/fshr/pages/${locale}/profile/my-stays/`
							: `/content/fshr/pages/${locale}/sign-in/`
					),
				},
	};
}

function PropertyIntroAndConversationCard({
	intro,
	positioning,
	conversationCardImage,
	propertyCode,
	propertyPath,
	interactionType,
	sectionId,
	userStore,
}: Props): ReactElement {
	const t = useTranslations();
	const locale = useLocale();
	const { trackLink } = useTealium();
	const { webChatContent } = useWebChatContent(locale, propertyCode);
	const { enabled: chatEnabled, openWebChat } = useWebChat({
		webChatContent,
		options: { hideFloatingChatButton: true },
	});
	const isMobile = useMediaQuery({ query: '(max-width: 960px)' });

	// The decision-engine script is loaded by the host page and is usually not
	// ready when this first renders; journeyStore resolves the stage once it is
	// and, being observed here, re-renders us. Until then getStage is undefined
	// and the phase falls back to discovery.
	useEffect(() => {
		journeyStore.track(propertyCode);
	}, [propertyCode]);

	const mostRecentSearch = journeyStore.getMostRecentSearch(propertyCode);
	const phase = resolvePhase(journeyStore.getStage(propertyCode));

	const { phoneNumber, locationHref, contactUsHref, ...introRest } = intro;
	const links = resolveIntroLinks({ phoneNumber, locationHref, contactUsHref }, chatEnabled, t);

	const isSignedIn = Boolean(userStore?.isSignedIn);
	// Live concierge chat requires full authentication. A persistently signed-in
	// (remembered) session has redacted profile data and cannot start a chat, so
	// treat chat as unavailable for it and fall back to the "Contact us" CTA.
	const chatAvailable = chatEnabled && !userStore?.isPersistentlySignedIn;
	const trackCta = (detail: string) => {
		if (!interactionType) return;
		trackLink('interaction', {
			interaction_type: interactionType,
			interaction_name: 'conversation card',
			interaction_detail: detail,
			journey_phase: phase,
			active_search: phase === 'shopping',
			layout_variant: getPlpLayoutVariant(),
		});
	};

	const iconList: IconListProps | undefined =
		positioning && positioning.length >= 3
			? {
					facts: positioning,
				}
			: undefined;
	return (
		<IntroAndConversationCard
			sectionId={sectionId}
			intro={{ ...introRest, links }}
			iconList={iconList}
			conversationCard={resolveConversationCard({
				image: conversationCardImage,
				phase,
				mostRecentSearch,
				chatAvailable,
				contactUsHref,
				isSignedIn,
				isMobile,
				locale,
				propertyCode,
				propertyPath,
				onChat: openWebChat,
				trackCta,
				t,
			})}
		/>
	);
}

export default observer(PropertyIntroAndConversationCard);
